import {
	ColumnFiltersState,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getSortedRowModel,
	SortingState,
	useReactTable,
} from "@tanstack/react-table";
import { useState } from "react";

interface PDAlertTableProps {
	data: any[];
	columns: any[];
	rowSelectionEnabled: boolean;
	onRowSelect: (row: any) => void;
	serverSideSearch?: boolean;
	onServerSearch?: (columnId: string, value: string) => void;
	serverSideSorting?: boolean;
	onServerSort?: (columnId: string, direction: 'asc' | 'desc' | false) => void;
	sortColumn?: string | null;
	sortDirection?: 'asc' | 'desc';
	isLoading?: boolean;
	containerStyle?: React.CSSProperties;
	showSearch?: boolean;
	selectedRowId?: number;
	selectedRowStyle?: React.CSSProperties;
	isRowDisabled?: (row: any) => boolean;
}

const AdminTables = ({
	data,
	columns,
	rowSelectionEnabled = false,
	onRowSelect,
	serverSideSearch = false,
	onServerSearch,
	serverSideSorting = false,
	onServerSort,
	sortColumn = null,
	sortDirection = 'asc',
	isLoading = false,
	containerStyle,
	showSearch = true,
	selectedRowId,
	selectedRowStyle,
	isRowDisabled,
}: PDAlertTableProps) => {
	const [sorting, setSorting] = useState<SortingState>([]);
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const [searchInputValues, setSearchInputValues] = useState<Record<string, string>>({});

	const table = useReactTable({
		data,
		columns: columns,
		state: {
			sorting,
			columnFilters
		},
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		// Only enable client-side sorting if server-side sorting is not enabled
		getSortedRowModel: serverSideSorting ? undefined : getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		// skip filtering if using server side search
		enableFilters: !serverSideSearch,
		// Disable sorting if server-side sorting is enabled
		enableSorting: !serverSideSorting,
		manualSorting: serverSideSorting,
	});

	const handleFilterChange = (columnId: string, value: string) => {
		// Update the input value state
		setSearchInputValues(prev => ({
			...prev,
			[columnId]: value
		}));
		
		if (serverSideSearch && onServerSearch) {
			// Call the server-side search handler
			onServerSearch(columnId, value);
			// Don't update columnFilters for server-side search
		} else {
			// Use client-side filtering
			setColumnFilters((old) => {
				const newFilters = old.filter((filter) => filter.id !== columnId);
				if (value) {
					newFilters.push({ id: columnId, value });
				}
				return newFilters;
			});
		}
	};

	const handleSortChange = (columnId: string) => {
		if (serverSideSorting && onServerSort) {
			// Determine the new sort direction
			let newDirection: 'asc' | 'desc' | false = 'asc';
			
			if (sortColumn === columnId) {
				// Toggle through asc -> desc -> no sort
				if (sortDirection === 'asc') {
					newDirection = 'desc';
				} else if (sortDirection === 'desc') {
					newDirection = false;
				}
			}
			
			// Call the server-side sort handler
			onServerSort(columnId, newDirection);
		}
	};

	const handleRowClick = (row: any) => {
		// Check if row is disabled
		if (isRowDisabled && isRowDisabled(row.original)) {
			return;
		}
		
		// Clear all selections first
		table.getRowModel().rows.forEach((r) => {
			if (r.getIsSelected()) {
				r.toggleSelected(false);
			}
		});
		// Select the clicked row
		row.toggleSelected(true);
		onRowSelect(row.original);
	};
	const defaultContainerStyle: React.CSSProperties = {
		width: "95vw",
		overflowX: "auto"
	};
	return (
		<div style={containerStyle || defaultContainerStyle}>
			<style>
				{`
					.table tbody tr.selected-row,
					.table tbody tr.selected-row:nth-of-type(odd),
					.table tbody tr.selected-row:nth-of-type(even) {
						background-color: ${selectedRowStyle?.backgroundColor || '#5376F0'} !important;
						color: ${selectedRowStyle?.color || '#FFFFFF'} !important;
					}
					
					.table tbody tr.selected-row td {
						background-color: ${selectedRowStyle?.backgroundColor || '#5376F0'} !important;
						color: ${selectedRowStyle?.color || '#FFFFFF'} !important;
					}
					
					.table tbody tr.selected-row:hover {
						background-color: ${selectedRowStyle?.backgroundColor || '#5376F0'} !important;
						color: ${selectedRowStyle?.color || '#FFFFFF'} !important;
						opacity: 0.9;
					}
					
					.table tbody tr.disabled-row {
						cursor: not-allowed !important;
					}
					
					.table tbody tr.disabled-row:hover {
						background-color: transparent !important;
					}
				`}
			</style>
			<table className="table" style={{ width: "100%", color: "#0156A8"}}>
				<thead>
					{table.getHeaderGroups().map((headerGroup) => (
						<tr key={headerGroup.id} style={{ color: "#0156A8" }}>
							{headerGroup.headers.map((header) => (
								<th
									key={header.id}
									style={{
										width: (header.column.columnDef as any).width,
										fontSize: "14px",
										fontWeight: 700,
										lineHeight: "17.6px",
										color: "#000000",
										paddingBottom: '0px',
									}}
								>
									<p 
									onClick={(e) => {
										if (serverSideSorting) {
											handleSortChange(header.id);
										} else {
											const handler = header.column.getToggleSortingHandler();
											if (handler) handler(e);
										}
									}}
									style={{ cursor: "pointer", width: "100%", marginBottom: '0px' }}
									>
										{flexRender(
											header.column.columnDef.header,
											header.getContext()
										)}
										{serverSideSorting ? (
											// Server-side sorting indicators
											sortColumn === header.id && sortDirection === "asc" ? (
												<i className="bi bi-sort-up mx-2"></i>
											) : sortColumn === header.id && sortDirection === "desc" ? (
												<i className="bi bi-sort-down mx-2"></i>
											) : null
										) : (
											// Client-side sorting indicators
											header.column.getIsSorted() === "asc" ? (
												<i className="bi bi-sort-up mx-2"></i>
											) : header.column.getIsSorted() === "desc" ? (
												<i className="bi bi-sort-down mx-2"></i>
											) : null
										)}
									</p>
									{showSearch && (
										<input
											className="form-control"
											value={searchInputValues[header.id] || ""}
											onChange={(e) =>
												handleFilterChange(header.id, e.target.value)
											}
											placeholder={`Search ${header.id.split("_").join(" ")}`}
										/>
									)}
								</th>
							))}
						</tr>
					))}
				</thead>

				{
					isLoading ? (
						<tr>
							<td colSpan={columns.length} style={{ textAlign: "center" }}>
								<div
									className="d-flex justify-content-center align-items-center"
									style={{ height: "200px" }}
								>
									<div className="spinner-border text-primary" role="status">
										<span className="visually-hidden">Loading...</span>
									</div>
								</div>
							</td>
						</tr>
					) : (data.length === 0 && (
						<tr>
							<td colSpan={columns.length} style={{ textAlign: "center" }}>
								<h3
									className="text-secondary"
									style={{
										textAlign: "center",
										marginTop: "7%",
										marginBottom: "5%",
									}}
								>
									No data available
								</h3>
							</td>
						</tr>
					))}
				
				{
					data.length > 0 && (
						<tbody>
							{table.getRowModel().rows.map((row) => {
								const isSelected = selectedRowId !== undefined && row.original.id === selectedRowId;
								const isDisabled = isRowDisabled && isRowDisabled(row.original);
								return (
									<tr
										key={row.id}
										onClick={() => handleRowClick(row)}
										className={isSelected ? 'selected-row' : (isDisabled ? 'disabled-row' : '')}
										style={{
											cursor: isDisabled ? "not-allowed" : "pointer",
											borderLeft: "1px solid #dee2e6",
											borderRight: "1px solid #dee2e6",
										}}
									>
										{row.getVisibleCells().map((cell) => (
											<td
												key={cell.id}
												style={{
													padding: "8px",
													paddingBottom: "10px",
													fontSize: "16px",
													fontWeight: 400,
													fontFamily: 'Roboto',
													color: '#1E293B'
												}}
											>
												{flexRender(cell.column.columnDef.cell, cell.getContext())}
											</td>
										))}
									</tr>
								);
							})}
						</tbody>
					)}
				</table>
			</div>
		);
	};

export default AdminTables;
